import React, { useState, useEffect } from 'react';
import { FileDown, Building2, User, Phone, CreditCard, Heart, Briefcase } from 'lucide-react';
import ExcelJS from 'exceljs';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

// Mock data - matching Form-A.xlsx template placeholders exactly (36 total - Employee Master Data)
const mockFormData = {
  // Establishment location
  est_location: "Bangalore",

  // Serial number
  sno: "1",

  // Employee basic information
  emp_id: "EMP001",
  emp_desg: "Senior Software Engineer",
  emp_dob: "1990-05-15",
  emp_doj: "2024-01-15",
  emp_age: "34",
  emp_gender: "Male",
  emp_marital: "Married",
  emp_father: "Ramesh Kumar",
  emp_mother: "Lakshmi Devi",
  emp_spouse: "Priya Kumar",

  // Contact information
  emp_address: "123 MG Road, Koramangala, Bangalore - 560034",
  emp_mob: "9876543210",
  emp_email: "rajesh.kumar@example.com",
  emp_emerno: "9988776655", // Emergency number

  // Identification documents
  emp_aadhaar: "1234 5678 9012",
  uan: "100123456789",
  pan: "ABCDE1234F",
  aadhaar: "1234 5678 9012",
  esic_no: "1234567890",
  conduct_cert: "Yes",
  blood_g: "O+",
  emp_R_status: "Active",
  emp_ed: "Bachelor of Engineering",

  // Banking details
  bank_name: "HDFC Bank",
  bank_acno: "12345678901234",
  ifsc_code: "HDFC0001234",

  // Next of kin
  next_kin1: "Priya Kumar (Wife) - 9988776655",
  next_kin2: "Ramesh Kumar (Father) - 9876543211",

  // Vaccination records
  vac1_date: "2021-05-15",
  vac2_date: "2021-06-30",
  boost_date: "2022-01-20",
  pvc_date: "2024-03-15", // Pre-employment vaccination certificate
  mc_date: "2024-01-10", // Medical certificate date
  pvc_rep_no: "PVC/2024/12345", // PVC report number

  // Registration email and contact
  reg_email: "hr@abcmanufacturing.com",
  reg_fax: "080-12345678",
  reg_phone: "080-87654321"
};

const FormAProfessionalTax = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    // Fetch data from API on component mount
    const fetchFormData = async () => {
      try {
        console.log('Fetching Form A (Employee Master) data from API...');
        // const response = await fetch('/api/form-a-data');
        // const data = await response.json();
        // setFormData(data);
        console.log('Using mock data for Form A');
      } catch (error) {
        console.error('Error fetching Form A data:', error);
        console.log('Falling back to mock data');
      }
    };

    fetchFormData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateExcel = async () => {
    setIsGenerating(true);

    try {
      console.log('Starting Form A (Employee Master) Excel generation...');
      console.log('Form Data:', formData);

      // Load the template
      const response = await fetch('/templates/Form-A.xlsx');
      const arrayBuffer = await response.arrayBuffer();

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];

      console.log('Template loaded successfully');

      // Remove ALL background fills from the entire worksheet
      worksheet.eachRow({ includeEmpty: true }, (row) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.fill) {
            cell.fill = null;
          }
        });
      });

      console.log('Background fills removed');

      // Single-pass placeholder replacement (all fields at form level)
      console.log('Replacing all placeholders...');
      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const cellValue = cell.value ? String(cell.value) : '';

          if (cellValue.includes('{{')) {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let newValue = cellValue;
            let match;

            while ((match = placeholderRegex.exec(cellValue)) !== null) {
              const fieldName = match[1].trim();
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');

              // Check all fields
              if (formData[normalizedFieldName] !== undefined) {
                console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${formData[normalizedFieldName]}"`);
                newValue = newValue.replace(match[0], formData[normalizedFieldName]);
              }
            }

            if (newValue !== cellValue) {
              cell.value = newValue;
            }
          }
        });
      });

      console.log('All placeholders replaced');

      // Generate Form file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Form-A-Employee-Master-${formData.emp_id}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('Excel file generated and downloaded successfully');

      setTimeout(() => {
        setIsGenerating(false);
        alert('Form A - Employee Master Data Excel generated successfully!');
      }, 500);

    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(`Error generating Excel: ${error.message}`);
      setIsGenerating(false);
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form A - Employee Master Data</h2>
          <p className="appointment-subtitle">Complete Employee Registration & Documentation</p>
        </div>
      </div>

      <div className="appointment-content">
        {/* Info Alert */}
        <div className="info-alert">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>Complete employee master data form. Fill all fields before generating the Excel.</span>
        </div>

        {/* Establishment & Basic Info */}
        <div className="form-section">
          <div className="section-header">
            <Building2 size={20} />
            <h3>Organization & Basic Information</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Organization Location</label>
              <input type="text" value={formData.est_location} onChange={(e) => handleInputChange('est_location', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Serial Number</label>
              <input type="text" value={formData.sno} onChange={(e) => handleInputChange('sno', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Employee ID</label>
              <input type="text" value={formData.emp_id} onChange={(e) => handleInputChange('emp_id', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Designation</label>
              <input type="text" value={formData.emp_desg} onChange={(e) => handleInputChange('emp_desg', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Status</label>
              <select value={formData.emp_R_status} onChange={(e) => handleInputChange('emp_R_status', e.target.value)}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="On Leave">On Leave</option>
                <option value="Terminated">Terminated</option>
              </select>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="form-section">
          <div className="section-header">
            <User size={20} />
            <h3>Personal Information</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Date of Birth</label>
              <DateInput value={formData.emp_dob} onChange={(e) => handleInputChange('emp_dob', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Age</label>
              <input type="number" value={formData.emp_age} onChange={(e) => handleInputChange('emp_age', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Gender</label>
              <select value={formData.emp_gender} onChange={(e) => handleInputChange('emp_gender', e.target.value)}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-field">
              <label>Marital Status</label>
              <select value={formData.emp_marital} onChange={(e) => handleInputChange('emp_marital', e.target.value)}>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>
            <div className="form-field">
              <label>Father's Name</label>
              <input type="text" value={formData.emp_father} onChange={(e) => handleInputChange('emp_father', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Mother's Name</label>
              <input type="text" value={formData.emp_mother} onChange={(e) => handleInputChange('emp_mother', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Spouse Name</label>
              <input type="text" value={formData.emp_spouse} onChange={(e) => handleInputChange('emp_spouse', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Education</label>
              <input type="text" value={formData.emp_ed} onChange={(e) => handleInputChange('emp_ed', e.target.value)} placeholder="Highest qualification" />
            </div>
            <div className="form-field">
              <label>Blood Group</label>
              <select value={formData.blood_g} onChange={(e) => handleInputChange('blood_g', e.target.value)}>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="form-section">
          <div className="section-header">
            <Phone size={20} />
            <h3>Contact Information</h3>
          </div>
          <div className="form-grid">
            <div className="form-field full-width">
              <label>Address</label>
              <textarea value={formData.emp_address} onChange={(e) => handleInputChange('emp_address', e.target.value)} rows={2} />
            </div>
            <div className="form-field">
              <label>Mobile Number</label>
              <input type="tel" value={formData.emp_mob} onChange={(e) => handleInputChange('emp_mob', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Email</label>
              <input type="email" value={formData.emp_email} onChange={(e) => handleInputChange('emp_email', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Emergency Contact Number</label>
              <input type="tel" value={formData.emp_emerno} onChange={(e) => handleInputChange('emp_emerno', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Identification Documents */}
        <div className="form-section">
          <div className="section-header">
            <CreditCard size={20} />
            <h3>Identification & Documentation</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Aadhaar Number</label>
              <input type="text" value={formData.emp_aadhaar} onChange={(e) => handleInputChange('emp_aadhaar', e.target.value)} placeholder="1234 5678 9012" />
            </div>
            <div className="form-field">
              <label>PAN Number</label>
              <input type="text" value={formData.pan} onChange={(e) => handleInputChange('pan', e.target.value)} placeholder="ABCDE1234F" />
            </div>
            <div className="form-field">
              <label>UAN Number</label>
              <input type="text" value={formData.uan} onChange={(e) => handleInputChange('uan', e.target.value)} placeholder="100123456789" />
            </div>
            <div className="form-field">
              <label>ESIC Number</label>
              <input type="text" value={formData.esic_no} onChange={(e) => handleInputChange('esic_no', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Conduct Certificate</label>
              <select value={formData.conduct_cert} onChange={(e) => handleInputChange('conduct_cert', e.target.value)}>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>
        </div>

        {/* Banking Details */}
        <div className="form-section">
          <div className="section-header">
            <CreditCard size={20} />
            <h3>Banking Details</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Bank Name</label>
              <input type="text" value={formData.bank_name} onChange={(e) => handleInputChange('bank_name', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Account Number</label>
              <input type="text" value={formData.bank_acno} onChange={(e) => handleInputChange('bank_acno', e.target.value)} />
            </div>
            <div className="form-field">
              <label>IFSC Code</label>
              <input type="text" value={formData.ifsc_code} onChange={(e) => handleInputChange('ifsc_code', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Employment Details */}
        <div className="form-section">
          <div className="section-header">
            <Briefcase size={20} />
            <h3>Employment Details</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Date of Joining</label>
              <DateInput value={formData.emp_doj} onChange={(e) => handleInputChange('emp_doj', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Next of Kin */}
        <div className="form-section">
          <div className="section-header">
            <User size={20} />
            <h3>Next of Kin / Emergency Contacts</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Next of Kin 1</label>
              <input type="text" value={formData.next_kin1} onChange={(e) => handleInputChange('next_kin1', e.target.value)} placeholder="Name (Relation) - Phone" />
            </div>
            <div className="form-field">
              <label>Next of Kin 2</label>
              <input type="text" value={formData.next_kin2} onChange={(e) => handleInputChange('next_kin2', e.target.value)} placeholder="Name (Relation) - Phone" />
            </div>
          </div>
        </div>

        {/* Medical & Vaccination Records */}
        <div className="form-section">
          <div className="section-header">
            <Heart size={20} />
            <h3>Medical & Vaccination Records</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Vaccination Dose 1 Date</label>
              <DateInput value={formData.vac1_date} onChange={(e) => handleInputChange('vac1_date', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Vaccination Dose 2 Date</label>
              <DateInput value={formData.vac2_date} onChange={(e) => handleInputChange('vac2_date', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Booster Dose Date</label>
              <DateInput value={formData.boost_date} onChange={(e) => handleInputChange('boost_date', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Pre-Employment Vaccination Certificate Date</label>
              <DateInput value={formData.pvc_date} onChange={(e) => handleInputChange('pvc_date', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Medical Certificate Date</label>
              <DateInput value={formData.mc_date} onChange={(e) => handleInputChange('mc_date', e.target.value)} />
            </div>
            <div className="form-field">
              <label>PVC Report Number</label>
              <input type="text" value={formData.pvc_rep_no} onChange={(e) => handleInputChange('pvc_rep_no', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Registration Contact Details */}
        <div className="form-section">
          <div className="section-header">
            <Phone size={20} />
            <h3>Registration Office Contact</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Registration Email</label>
              <input type="email" value={formData.reg_email} onChange={(e) => handleInputChange('reg_email', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Registration Phone</label>
              <input type="tel" value={formData.reg_phone} onChange={(e) => handleInputChange('reg_phone', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Registration Fax</label>
              <input type="text" value={formData.reg_fax} onChange={(e) => handleInputChange('reg_fax', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Generate Form Button */}
        <div className="form-actions">
          <button
            onClick={generateExcel}
            disabled={isGenerating}
            className={`btn-generate ${isGenerating ? 'generating' : ''}`}
          >
            {isGenerating ? (
              <>
                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" opacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                </svg>
                <span>Generating Excel...</span>
              </>
            ) : (
              <>
                <FileDown size={20} />
                <span>Generate & Download Form A Employee Master Excel</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormAProfessionalTax;
